import React, { useRef } from 'react';
import { CircularProgress } from '@material-ui/core';
import translate from '../../locales';
import { useEffect, useState } from 'react';
import { styleWithCommonClasses } from '../../_constants/emparkConstants';

import TitleToolbar from '../Forms/TitleToolbar';
import { useExecution } from '../../hooks/useExecution';
import { createRule, deleteRule, getRules, updateRule } from '../../services/parametersService';
import ListItem from '../ListItem';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import LoadingDialog from '../Dialogs/LoadingDialog';
import { getBuckets } from '../../services/bucketsService';
import RuleMappingForm from '../Forms/Parameters/RuleMappingForm';
import RuleMappingPreview from '../Parameters/RuleMappingPreview';
import { jsonDateStrToDisplay, toDateJsonString } from '../../_helpers/dateConvert';
import { connect } from 'react-redux';
import { contextsActions } from '../../actions/contextsActions';

const __t = translate;

const styles = styleWithCommonClasses({
    paddingOnTop: {
        paddingTop: '20px'
    },
    upperCase: {
        textTransform: 'uppercase'
    },
    containerSelect: {
        width: '100%',
        padding: '4px'
    },
    errorBorder: {
        border: '1px solid red',
        borderRadius: '5px'
    },
})

const RuleMappingController = (props) => {
    const [ruleMappings, setRuleMappings] = useState([]);
    const [buckets, setBuckets] = useState([]);
    const [error, setError] = useState(null);

    const fetchRuleMappingsQuery = useExecution(getRules);
    const fetchBucketsQuery = useExecution(getBuckets);

    useEffect(() => {
        fetchRuleMappingsQuery.execute(props.contextId, props.parameterId)
            .then(data => {
                setRuleMappings(data);
            })
            .catch(err => {
                console.error('Error fetching rule mappings:', err);
                setError('Unexpected Error');
            });

        fetchBucketsQuery.execute(props.contextId)
            .then(data => {
                setBuckets(data);
            })
            .catch(err => {
                console.error('Error fetching buckets:', err);
                setError('Unexpected Error');
            });
    }, [props.contextId]);

    if (fetchRuleMappingsQuery.loading || fetchBucketsQuery.loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <CircularProgress color='secondary' />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>{error}</div>
            </div>
        );
    }

    return (
        <>
            <RuleMappingPage
                contextId={props.contextId}
                parameterId={props.parameterId}
                buckets={buckets}
                ruleMappings={ruleMappings}
                setRuleMappings={setRuleMappings}
                setMessage={props.setMessage}
                setContextAsDraft={props.setContextAsDraft}
            />
        </>
    )
}

const RuleMappingPage = (props) => {
    const [mode, setMode] = useState({
        type: 'table', // table | view | add | edit
        ruleMapping: null
    });

    const [deleteModal, setDeleteModal] = useState(null);

    const createRuleMappingQuery = useExecution(createRule);
    const updateRuleMappingQuery = useExecution(updateRule);
    const deleteRuleMappingQuery = useExecution(deleteRule);

    const formRef = useRef(null);

    if (mode.type === 'table') {
        return (
            <div>
                <LoadingDialog loading={deleteRuleMappingQuery.loading} />
                <ConfirmDialog
                    open={deleteModal != null && !deleteRuleMappingQuery.loading}
                    title={__t('confirm_delete')}
                    onClose={(action) => {
                        if (action != 'ok') {
                            setDeleteModal(null);
                            return;
                        }

                        deleteRuleMappingQuery.execute(props.contextId, props.parameterId, deleteModal.id)
                            .then(() => {
                                props.setRuleMappings(props.ruleMappings.filter(rm => rm.id !== deleteModal.id));
                                setDeleteModal(null);

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                    }}
                />
                <TitleToolbar
                    title={__t('rules')}
                    onAdd={() => {
                        setMode({ type: 'add', ruleMapping: null });
                    }}
                >
                    {props.ruleMappings.map(rm =>
                        <ListItem
                            key={rm.id}
                            onView={() => {
                                setMode({ type: 'view', ruleMapping: rm });
                            }}
                            onDelete={() => {
                                setDeleteModal(rm);
                            }}
                        >
                            <RuleMappingPreview
                                name={rm.name}
                                bucketForcedRotation={rm.bucketForcedRotation ? rm.bucketForcedRotation.name : ''}
                                bucketFreePeriod={rm.bucketFreePeriod ? rm.bucketFreePeriod.name : ''}
                                multiplier={rm.multiplier}
                                step={rm.step}
                                roundingType={rm.roundingType}
                                periods={rm.periods.map(p => `[${jsonDateStrToDisplay(p.fromDate)} - ${jsonDateStrToDisplay(p.toDate)}]`).join(' ')}
                            />
                        </ListItem>)}
                </TitleToolbar>
            </div >
        )
    }

    return (
        <div>
            <LoadingDialog loading={createRuleMappingQuery.loading || updateRuleMappingQuery.loading} />
            <TitleToolbar
                title={`${__t(mode.type)} ${__t('rule')}`}
                onBack={mode.type == 'view' && (() => {
                    setMode({ type: 'table', ruleMapping: null });
                })}
                onEdit={mode.type == 'view' && (() => {
                    setMode({ type: 'edit', ruleMapping: mode.ruleMapping });
                })}
                onSave={(mode.type == 'edit' || mode.type == 'add') && (() => {
                    const formData = formRef.current.getData();
                    if (!formData)
                        return;

                    let ruleMappingData = {
                        name: formData.name,
                        description: formData.description,
                        periods: formData.periods.map(p => ({ fromDate: toDateJsonString(p.fromDate), toDate: toDateJsonString(p.toDate) }))
                    };

                    if (formData.bucketForcedRotationId)
                        ruleMappingData.bucketForcedRotationId = formData.bucketForcedRotationId;

                    if (formData.bucketFreePeriodId)
                        ruleMappingData.bucketFreePeriodId = formData.bucketFreePeriodId;

                    if (formData.multiplier.value)
                        ruleMappingData.multiplier = Number(formData.multiplier.value);

                    if (formData.multiplier.step)
                        ruleMappingData.step = String(formData.multiplier.step);

                    if (formData.multiplier.roundingType)
                        ruleMappingData.roundingType = formData.multiplier.roundingType;


                    if (mode.type === 'add') {
                        createRuleMappingQuery.execute(props.contextId, props.parameterId, ruleMappingData)
                            .then((newRuleMapping) => {
                                props.setRuleMappings([...props.ruleMappings, newRuleMapping]);
                                setMode({ type: 'table', ruleMapping: null });

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                    } else if (mode.type === 'edit') {
                        updateRuleMappingQuery.execute(props.contextId, props.parameterId, mode.ruleMapping.id, ruleMappingData)
                            .then((updatedRuleMapping) => {
                                props.setRuleMappings(props.ruleMappings.map(rm => rm.id === updatedRuleMapping.id ? updatedRuleMapping : rm));
                                setMode({ type: 'table', ruleMapping: null });

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                    }
                })}
                onCancel={(mode.type == 'edit' || mode.type == 'add') && (() => {
                    if (mode.type === 'add') {
                        setMode({ type: 'table', ruleMapping: null });
                        return;
                    }

                    setMode({ type: 'view', ruleMapping: mode.ruleMapping });
                })}
            >
                <RuleMappingForm
                    key={'form-rulemapping-' + mode.type}
                    ref={formRef}
                    buckets={props.buckets}
                    ruleMapping={mode.ruleMapping}
                    readonly={mode.type === 'view'}
                />
            </TitleToolbar>
        </div >
    )
}

export default connect(
    () => ({}),
    (dispatch) => ({
        setContextAsDraft: () => {
            dispatch(contextsActions.setAsDraft());
        }
    }))(RuleMappingController);